/** Includes clipping by the rail/pane scroll container, not just the window. */
export function isTourElementVisible(element: HTMLElement): boolean {
  if (!element.isConnected || element.closest('[inert]')) return false;
  const rect = element.getBoundingClientRect();
  if (
    rect.width <= 0 ||
    rect.height <= 0 ||
    rect.left < -1 ||
    rect.top < -1 ||
    rect.right > window.innerWidth + 1 ||
    rect.bottom > window.innerHeight + 1
  )
    return false;

  for (
    let parent: HTMLElement | null = element;
    parent;
    parent =
      parent.parentElement ??
      (parent.getRootNode() instanceof ShadowRoot
        ? ((parent.getRootNode() as ShadowRoot).host as HTMLElement)
        : null)
  ) {
    if (parent.hasAttribute('inert')) return false;
    const style = getComputedStyle(parent);
    if (style.visibility === 'hidden' || Number(style.opacity) < 0.99)
      return false;
    if (parent === element) continue;
    const bounds = parent.getBoundingClientRect();
    if (
      /(auto|scroll|hidden|clip)/.test(style.overflowY) &&
      (rect.top < bounds.top - 1 || rect.bottom > bounds.bottom + 1)
    )
      return false;
    if (
      /(auto|scroll|hidden|clip)/.test(style.overflowX) &&
      (rect.left < bounds.left - 1 || rect.right > bounds.right + 1)
    )
      return false;
  }
  return true;
}

/** Let mounting, smooth scrolling and pane animations finish before the hint. */
export async function waitForTourElementVisible(
  selector: string | (() => HTMLElement | null),
  signal?: AbortSignal,
  scrollIntoView?: ScrollIntoViewOptions
): Promise<HTMLElement> {
  const deadline = performance.now() + 10000;
  let previousRect: DOMRect | undefined;
  let stableFrames = 0;
  let scrolledElement: HTMLElement | null = null;
  while (performance.now() < deadline) {
    signal?.throwIfAborted();
    const element =
      typeof selector === 'string'
        ? document.querySelector<HTMLElement>(selector)
        : selector();
    if (element && scrollIntoView && element !== scrolledElement) {
      element.scrollIntoView(scrollIntoView);
      scrolledElement = element;
    }
    const rect = element?.getBoundingClientRect();
    const settled =
      rect &&
      previousRect &&
      Math.abs(rect.x - previousRect.x) < 0.5 &&
      Math.abs(rect.y - previousRect.y) < 0.5 &&
      Math.abs(rect.width - previousRect.width) < 0.5 &&
      Math.abs(rect.height - previousRect.height) < 0.5;
    stableFrames =
      element && isTourElementVisible(element) && settled
        ? stableFrames + 1
        : 0;
    if (element && stableFrames >= 3) return element;
    previousRect = rect;
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  }
  throw new Error(
    `Tutorial target did not become visible: ${typeof selector === 'string' ? selector : 'resolved target'}`
  );
}
